import { useSelector } from 'react-redux';
import { OnboardingChrome, SlideOutSheet } from '../components';
import { selectClient } from '../model';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import { formatCurrency } from '@shared/formatCurrency';
import { SlideContent } from '../components/SlideContent';
import { getFirstCarePhasePage } from '../util/navigationLogic';
import { OnboardingResultsAreIn, cn } from '..';
import FormattedList from '@/components/FormattedList';
import {
  formatPercent,
  formatThousands,
  CareEnvironment,
  CarePhase,
  isDurationCustom,
  areNumbersClose,
  defaultHealthcareInflation,
  isNullOrUndefined,
  appliedHealthCareInflation,
} from '@shared';
import {
  CalendarFoldIcon,
  CalendarHeartIcon,
  ChartNoAxesCombinedIcon,
  CheckIcon,
  CircleDollarSignIcon,
  FileCheckIcon,
  HeartHandshakeIcon,
  LandmarkIcon,
  MapPinHouseIcon,
  TagIcon,
} from 'lucide-react';
import { useState } from 'react';
import { EditIconButton } from '@/components/EditIconButton';
import EditHealthCareInflationModal from '@client/components/EditHealthCareInflationModal';
import { HealthcareInflationCitation } from '@client/components/HealthcareInflationCitation';
import { CostDataMethodologySlideOutContent } from '@/components/LearnMoreSlideOutContent';
import { BsFillQuestionCircleFill } from 'react-icons/bs';

export function OnboardingCostFactors({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const {
    clientFirstName,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    intakeSurvey: { clientHasStartedLtc },
  } = client;

  return (
    <OnboardingChrome
      slidePage={OnboardingCostFactors}
      nextButtonLabel={`Refine ${clientFirstName}'s Cost`}
      nextSlidePage={getFirstCarePhasePage(client)}
      previousSlidePage={clientHasStartedLtc ? OnboardingResultsAreIn : undefined}
    >
      <SlideContent
        headerText={
          <>
            <span data-testid="cost-factors-header">
              {clientFirstName}&apos;s estimated{clientHasStartedLtc ? ' remaining' : ''} long-term
              care costs
            </span>
          </>
        }
      >
        <div className="mb-12 mt-6 md:mb-8 md:mt-8">
          <p className="w-full text-right text-sm text-gray-600">Expand for detail</p>
          <CostFactorAccordion className="mb-4 mt-2" />
          <div
            className="flex justify-between"
            data-testid="cost-factors-total"
          >
            <label className="flex flex-col text-base text-gray-800 md:text-2xl">
              Estimated total costs
              <p className="whitespace-nowrap text-base text-gray-500 md:text-lg">
                *without a funding plan
              </p>
            </label>
            <p
              className="mr-11 text-base font-semibold text-gray-800 md:text-2xl"
              data-testid="cost-factors-total-value"
            >
              {formatCurrency(allPhaseInflatedProfessionalShareCost)}
            </p>
          </div>
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}

type CostFactorAccordionProps = {
  className?: string;
};

function CostFactorAccordion({ className }: CostFactorAccordionProps) {
  const client = useSelector(selectClient)!;
  const {
    allPhaseCosts: {
      allPhaseTotalCost,
      allPhaseInflatedFamilyCareCost,
      allPhaseInflatedProfessionalShareCost,
      allPhaseCareMonthsNeeded,
      allPhaseFamilyProvidedPercent,
      allPhaseFamilyCareHoursProvided,
    },
    intakeSurvey: {
      clientIndependencePreference,
      clientFinancialSecurityPreference,
      clientFamilyInvolvementPreference,
      clientHasStartedLtc,
    },
    clientFirstName,
    clientPhasePredictedEndYears,
    phaseCosts,
    clientCalculationSettings: { healthCareInflationRate: clientHealthCareInflationRate },
    clientLocalCareEnvironmentCosts,
  } = client;

  const allPhaseInflatedCost =
    allPhaseInflatedProfessionalShareCost + allPhaseInflatedFamilyCareCost;
  const healthCareInflationContribution = allPhaseInflatedCost - allPhaseTotalCost;

  const defaultInflationRate = defaultHealthcareInflation(client);
  const appliedInflationRate = appliedHealthCareInflation(client);
  const isInflationEdited =
    !isNullOrUndefined(clientHealthCareInflationRate) &&
    !areNumbersClose(clientHealthCareInflationRate, defaultInflationRate);

  const rateLocaleType = clientLocalCareEnvironmentCosts[CareEnvironment.home].rateLocaleType;
  const appliedZipCode = clientLocalCareEnvironmentCosts[CareEnvironment.home].zipCode;

  const careDurationEdited = isDurationCustom(phaseCosts);

  const [editModalOpen, setEditModalOpen] = useState(false);

  const costFactorsData = [
    {
      id: 1,
      title: `${clientHasStartedLtc ? 'Cost of Remaining Care' : 'Cost of Care'}`,
      cost: allPhaseTotalCost,
      changeType: 'increase',
    },
    {
      id: 2,
      title: 'Healthcare Inflation',
      cost: healthCareInflationContribution,
      changeType: 'increase',
    },
    {
      id: 3,
      title: 'Family Caregiving',
      cost: allPhaseInflatedFamilyCareCost,
      changeType: 'decrease',
      isLast: true,
    },
  ];

  return (
    <>
      <Accordion
        type="single"
        collapsible
        className={cn(className)}
      >
        {costFactorsData.map(costFactor => (
          <AccordionItem
            key={costFactor.id}
            value={`item-${costFactor.id}`}
            className={cn(`${costFactor.isLast ? 'border-b-4' : ''}`)}
          >
            <AccordionTrigger className="px-0">
              <AccordionTriggerContent
                title={costFactor.title}
                cost={costFactor.cost}
                changeType={costFactor.changeType as 'increase' | 'decrease'}
              />
            </AccordionTrigger>
            <AccordionContent className="text-base md:text-lg">
              {costFactor.id === 1 && (
                <div className="">
                  <h2 className="mb-1 text-sm text-gray-500 md:text-base">
                    Some of {clientFirstName}'s cost factors:
                  </h2>
                  <FormattedList
                    items={getCareSettingsContent(
                      clientFirstName,
                      rateLocaleType,
                      appliedZipCode,
                      allPhaseCareMonthsNeeded / 12,
                      clientIndependencePreference,
                      clientFinancialSecurityPreference,
                      clientHasStartedLtc,
                      careDurationEdited,
                    )}
                    className="p-2 text-sm md:text-base"
                  />
                </div>
              )}
              {costFactor.id === 2 && (
                <div className="space-y-1">
                  <h2 className="mb-1 text-sm text-gray-500 md:text-base">
                    The total projected cost increase is calculated using:
                  </h2>
                  <FormattedList
                    items={getHealthcareInflationContent(
                      allPhaseTotalCost,
                      Math.round(clientPhasePredictedEndYears[CarePhase.fullCare]!),
                      appliedInflationRate,
                      isInflationEdited,
                      healthCareInflationContribution,
                      setEditModalOpen,
                    )}
                    className="p-2 text-sm md:text-base"
                  />

                  <FootNote className="flex gap-1">
                    <p>1. </p>
                    <HealthcareInflationCitation client={client} />
                  </FootNote>

                  <FootNote className="-translate-y-1">
                    <p>
                      2. This simplified explanation is for educational purposes only and may
                      slightly overestimate the actual calculation used to estimate cost.
                    </p>
                  </FootNote>
                </div>
              )}
              {costFactor.id === 3 && (
                <div className="space-y-1">
                  <h2 className="mb-1 text-sm text-gray-500 md:text-base">
                    Family caregiving estimates based on client preferences and family
                    structure.{' '}
                  </h2>
                  <FormattedList
                    items={getFamilyInvolvementContent(
                      clientFirstName,
                      clientFamilyInvolvementPreference,
                      allPhaseFamilyProvidedPercent,
                      allPhaseFamilyCareHoursProvided,
                      allPhaseInflatedFamilyCareCost,
                      rateLocaleType,
                      appliedZipCode,
                    )}
                    className="p-2 text-sm md:text-base"
                  />
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <EditHealthCareInflationModal
        open={editModalOpen}
        setOpen={setEditModalOpen}
      />
    </>
  );
}

function FootNote({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('inline-block text-xs leading-snug text-gray-500', className)}>
      {children}
    </div>
  );
}

type AccordionTriggerContentProps = {
  title: string;
  cost: number;
  changeType: 'increase' | 'decrease';
};

function AccordionTriggerContent({ title, cost, changeType }: AccordionTriggerContentProps) {
  return (
    <div className="flex w-full items-center justify-between text-base font-normal md:text-2xl">
      <div className="text-left text-gray-900">
        <span className="text-gray-700">{title}</span>
      </div>
      <div className="flex h-7 items-center px-6">
        {changeType === 'decrease' ? (
          <span className="text-green-700">-{formatCurrency(cost)}</span>
        ) : (
          <span className="text-red-700">+{formatCurrency(cost)}</span>
        )}
      </div>
    </div>
  );
}

const getHealthcareInflationContent = (
  currentCost: number,
  projectedEndYear: number,
  inflationRate: number,
  isInflationEdited: boolean,
  healthCareInflationContribution: number,
  setEditModalOpen: (open: boolean) => void,
) => [
  {
    label: `Current cost: `,
    value: formatCurrency(currentCost),
    icon: <TagIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base',
  },
  {
    label: `Projected care end year: `,
    value: projectedEndYear,
    icon: <CalendarHeartIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base',
  },
  {
    label: `${isInflationEdited ? 'Edited healthcare inflation¹: ' : 'Healthcare inflation¹: '} `,
    value: (
      <span className="flex items-center">
        <span>{`${formatPercent(inflationRate, 1)} annually`}</span>
        <EditIconButton
          onClick={() => setEditModalOpen(true)}
          tooltipText="Edit healthcare inflation rate"
        />
      </span>
    ),
    icon: <LandmarkIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base',
  },
  {
    label: 'Future projected cost²: ',
    value: formatCurrency(currentCost + healthCareInflationContribution),
    icon: <ChartNoAxesCombinedIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base',
  },
];

const getCareSettingsContent = (
  clientFirstName: string,
  rateLocaleType: RateLocaleType,
  zipCode: string,
  ltcDurationYears: number,
  clientIndependencePreference: string,
  clientFinancialSecurityPreference: string,
  clientHasStartedLtc: boolean,
  careDurationEdited: boolean,
) => [
  {
    label: `${careDurationEdited ? 'Edited' : 'Projected'} care timeline`,
    value: ` ${formatThousands(ltcDurationYears, '', 1)} years ${clientHasStartedLtc ? 'remaining' : ''}`,
    icon: <CalendarFoldIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base',
  },
  {
    label: 'Market rates for care in',
    value: (
      <span className="flex items-center gap-1">
        {localCostString(rateLocaleType, zipCode)}
        <SlideOutSheet
          slideOutContent={<CostDataMethodologySlideOutContent />}
          title="Cost Data"
        >
          <BsFillQuestionCircleFill className="h-3 w-3 -translate-y-0.5 text-gray-500 hover:cursor-pointer hover:text-gray-800" />
        </SlideOutSheet>
      </span>
    ),
    icon: <MapPinHouseIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base',
  },
  {
    label: `${clientFirstName}'s preferences:`,
    subItems: [
      <span className="text-sm md:text-base">{clientIndependencePreference}</span>,
      <span className="text-sm md:text-base">{clientFinancialSecurityPreference}</span>,
    ],
    icon: <FileCheckIcon className="h-4 w-4" />,
    subBulletIcon: <CheckIcon className="h-3 w-3" />,
    className: 'text-sm md:text-base',
  },
];

const getFamilyInvolvementContent = (
  clientFirstName: string,
  clientFamilyInvolvementPreference: string,
  allPhaseFamilyProvidedPercent: number,
  allPhaseFamilyCareHoursProvided: number,
  allPhaseInflatedFamilyCareCost: number,
  rateLocaleType: RateLocaleType,
  zipCode: string,
) => [
  {
    label: `${clientFirstName}'s preferences: `,
    subItems: [<span className="text-sm md:text-base">{clientFamilyInvolvementPreference}</span>],
    icon: <FileCheckIcon className="h-4 w-4" />,
    subBulletIcon: <CheckIcon className="h-3 w-3" />,
    className: 'text-sm md:text-base',
  },
  {
    label: 'Projected family caregiving: ',
    value: `${formatThousands(allPhaseFamilyCareHoursProvided, '', 0)} hours (${formatPercent(allPhaseFamilyProvidedPercent, 1)} of care needs)`,
    icon: <HeartHandshakeIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base',
  },
  {
    label: 'Projected value of care provided by family: ',
    value: `${formatCurrency(allPhaseInflatedFamilyCareCost)}`,
    icon: <CircleDollarSignIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base flex-wrap break-all min-w-0',
  },
  {
    label: 'Based on market rates for home care in ',
    value: `${localCostString(rateLocaleType, zipCode)}`,
    icon: <MapPinHouseIcon className="h-4 w-4" />,
    className: 'text-sm md:text-base flex-wrap break-all min-w-0',
  },
];

function localCostString(rateLocaleType: RateLocaleType, zipCode: string) {
  switch (rateLocaleType) {
    case 'National':
      return 'the US';
    case 'State':
      return 'your state';
    case 'Local':
      return `zip code ${zipCode}`;
  }
}
